import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Building2,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Printer,
  RefreshCw,
  Search,
  Users,
  X,
  Filter,
  Eye,
} from 'lucide-react';

import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { DebouncedInput } from '@/components/DebouncedInput';
import { MultiSelect } from '@/components/MultiSelect';
import { useResellerCoverageReportQuery } from '@/queries/useResellerCoverageReportQuery';
import { useMarketResearchQuery } from '@/queries/useMarketResearchQuery';
import { useResaleQuery } from '@/queries/useResaleQuery';
import { useBaseStore } from '@/store';
import { Store } from '@/store/type';
import LoadingScreen from '@/pages/LoadingScreen';
import ResellerCoverageItemsDialog from './components/ResellerCoverageItemsDialog';
import { ResellerCoverageItem, FetchResellerCoveragePayload } from '@/api/business/marketResearch';

const stateSelector = (state: Store) => ({
  profile: state.authSlice.state.profile,
});

const ResellerCoverageReport = () => {
  const [searchParams] = useSearchParams();
  const initialResearchId = searchParams.get('search')
    ? Number(searchParams.get('search'))
    : 0;

  const { profile } = useBaseStore(stateSelector);

  const [selectedPesquisas, setSelectedPesquisas] = useState<string[]>(
    initialResearchId > 0 ? [String(initialResearchId)] : []
  );
  const [selectedResales, setSelectedResales] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modal state for reseller detail items
  const [selectedResellerForItems, setSelectedResellerForItems] =
    useState<ResellerCoverageItem | null>(null);
  const [isItemsDialogOpen, setIsItemsDialogOpen] = useState<boolean>(false);

  // Initial search activation state
  const [hasSearched, setHasSearched] = useState<boolean>(initialResearchId > 0);
  const [appliedPayload, setAppliedPayload] = useState<FetchResellerCoveragePayload>(() => {
    const p: FetchResellerCoveragePayload = {};
    if (initialResearchId > 0) {
      p.SK_PESQUISA = initialResearchId;
      p.SK_PESQUISAS = [initialResearchId];
    }
    return p;
  });

  // Fetch drop options
  const { data: researches } = useMarketResearchQuery();
  const { data: resales } = useResaleQuery();

  const {
    data: reportData,
    isLoading,
    isFetching,
    refetch,
  } = useResellerCoverageReportQuery(appliedPayload, {
    enabled: hasSearched,
  });

  const handleTogglePesquisa = (val: string) => {
    setSelectedPesquisas((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleSelectAllPesquisas = (values: string[]) => {
    setSelectedPesquisas((prev) => (prev.length === values.length ? [] : values));
  };

  const handleToggleResale = (val: string) => {
    setSelectedResales((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleSelectAllResales = (values: string[]) => {
    setSelectedResales((prev) => (prev.length === values.length ? [] : values));
  };

  const handleSearch = () => {
    const p: FetchResellerCoveragePayload = {};
    if (selectedPesquisas.length > 0) {
      p.SK_PESQUISAS = selectedPesquisas.map(Number);
    }
    if (selectedResales.length > 0) {
      p.SK_REVENDAS = selectedResales.map(Number);
    }
    if (searchTerm.trim()) {
      p.SEARCH_TERM = searchTerm.trim();
    }
    setAppliedPayload(p);
    setHasSearched(true);
  };

  // Options for Pesquisa MultiSelect
  const pesquisaOptions = useMemo(() => {
    if (!researches) return [];
    return researches.map((r) => ({
      value: String(r.SK_PESQUISA),
      label: `${r.NM_PESQUISA} (${r.SK_PESQUISA})`,
    }));
  }, [researches]);

  // Options for Resale MultiSelect
  const resaleOptions = useMemo(() => {
    if (!resales) return [];
    return resales.map((r) => ({
      value: String(r.SK_REVENDA),
      label: r.DS_REVENDA,
    }));
  }, [resales]);

  const filteredReport = hasSearched && reportData ? reportData : [];

  // Totalizers (RF002 / RN002 / RN004)
  const totals = useMemo(() => {
    const previstos = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_CLIENTES_PREVISTOS || 0),
      0,
    );
    const realizadas = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_PESQUISAS_REALIZADAS || 0),
      0,
    );
    const pendentes = filteredReport.reduce(
      (acc, curr) => acc + (curr.QTD_PESQUISAS_PENDENTES || 0),
      0,
    );
    const cobertura =
      previstos > 0 ? Number(((realizadas / previstos) * 100).toFixed(2)) : 0;
    const totalResellerCount = new Set(filteredReport.map((i) => i.SK_REVENDA))
      .size;

    return {
      previstos,
      realizadas,
      pendentes,
      cobertura,
      totalResellerCount,
    };
  }, [filteredReport]);

  // Export to Excel (RF005 / RN003)
  const handleExportExcel = () => {
    if (!filteredReport || filteredReport.length === 0) return;

    const exportData = filteredReport.map((item) => ({
      'Código Revenda': item.SK_REVENDA,
      'Nome Revenda': item.DS_REVENDA,
      Pesquisa: item.NM_PESQUISA,
      'Clientes Previstos': item.QTD_CLIENTES_PREVISTOS,
      'Pesquisas Realizadas': item.QTD_PESQUISAS_REALIZADAS,
      'Pesquisas Pendentes': item.QTD_PESQUISAS_PENDENTES,
      '% Cobertura': `${item.PERC_COBERTURA.toFixed(2)}%`,
    }));

    // Append total row
    exportData.push({
      //@ts-ignore
      'Código Revenda': 'TOTAL GERAL',
      'Nome Revenda': `${totals.totalResellerCount} Revendas`,
      Pesquisa: '-',
      'Clientes Previstos': totals.previstos,
      'Pesquisas Realizadas': totals.realizadas,
      'Pesquisas Pendentes': totals.pendentes,
      '% Cobertura': `${totals.cobertura.toFixed(2)}%`,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cobertura por Revenda');

    // Auto-fit column widths
    const maxWidths = [15, 30, 30, 18, 20, 20, 15];
    worksheet['!cols'] = maxWidths.map((w) => ({ wch: w }));

    XLSX.writeFile(
      workbook,
      `Relatorio_Cobertura_Revenda_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  // Print functionality (RF005)
  const handlePrint = () => {
    window.print();
  };

  const handleClearFilters = () => {
    setSelectedPesquisas([]);
    setSelectedResales([]);
    setSearchTerm('');
    setAppliedPayload({});
    setHasSearched(false);
  };

  const handleOpenItemsDialog = (item: ResellerCoverageItem) => {
    setSelectedResellerForItems(item);
    setIsItemsDialogOpen(true);
  };

  if (hasSearched && isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-[1600px] mx-auto print:p-0 print:space-y-4">
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-4 print:border-b-2 print:border-black">
        <div>
          <TypographyH1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary print:hidden" />
            Relatório Consolidado por Revenda
          </TypographyH1>
          <TypographyMuted className="text-sm text-gray-500 mt-1">
            Acompanhamento consolidado da evolução de execução e cobertura das
            pesquisas de mercado por revenda.
          </TypographyMuted>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 text-xs"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`}
            />
            Atualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={!filteredReport || filteredReport.length === 0}
            className="gap-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            Exportar Excel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handlePrint}
            disabled={!filteredReport || filteredReport.length === 0}
            className="gap-2 text-xs"
          >
            <Printer className="w-3.5 h-3.5" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Filter Card */}
      <Card className="print:hidden border border-gray-200 shadow-sm bg-white">
        <CardHeader className="py-3 px-4 bg-gray-50/50 border-b">
          <CardTitle className="text-sm font-semibold flex items-center justify-between text-gray-700">
            <span className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-primary" />
              Filtros de Consulta
            </span>
            {(selectedPesquisas.length > 0 ||
              selectedResales.length > 0 ||
              searchTerm !== '' ||
              hasSearched) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-7 text-xs text-gray-500 hover:text-red-600 px-2"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Limpar Filtros
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
            {/* Filter Pesquisa MultiSelect */}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Pesquisas ({selectedPesquisas.length > 0 ? selectedPesquisas.length : 'Todas'})
              </label>
              <MultiSelect
                label="Todas as Pesquisas"
                placeholder="Buscar pesquisa..."
                emptyLabel="Nenhuma pesquisa encontrada"
                options={pesquisaOptions}
                selectedValues={selectedPesquisas}
                onChange={handleTogglePesquisa}
                onSelectAll={handleSelectAllPesquisas}
              />
            </div>

            {/* Filter Resale MultiSelect (Only editable if profile is ADMINISTRADOR) */}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Revendas ({selectedResales.length > 0 ? selectedResales.length : 'Todas'})
              </label>
              <MultiSelect
                label="Todas as Revendas"
                placeholder="Buscar revenda..."
                emptyLabel="Nenhuma revenda encontrada"
                options={resaleOptions}
                selectedValues={selectedResales}
                onChange={handleToggleResale}
                onSelectAll={handleSelectAllResales}
                disabled={
                  profile === 'ADMINISTRADOR_REVENDA' ||
                  profile === 'REPRESENTANTE'
                }
              />
            </div>

            {/* Search Input */}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                Buscar por Revenda ou Pesquisa
              </label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <DebouncedInput
                  value={searchTerm}
                  onChange={(val) => setSearchTerm(String(val))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="Digite nome da revenda..."
                  className="pl-9 w-full text-xs h-10 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Search Button */}
            <div>
              <Button
                onClick={handleSearch}
                disabled={isFetching}
                className="w-full h-10 gap-2 text-xs bg-primary text-white hover:bg-primary/90 shadow-sm"
              >
                <Search className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                <span>{isFetching ? 'Consultando...' : 'Consultar'}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totalizer KPI Cards (RF002 / RN002 / RN004) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Revendas */}
        <Card className="border border-blue-100 bg-gradient-to-br from-blue-50/50 to-white shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <TypographyMuted className="text-xs font-medium text-blue-600 uppercase tracking-wider">
                Revendas
              </TypographyMuted>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totals.totalResellerCount}
              </p>
            </div>
            <div className="p-3 bg-blue-100/80 text-blue-700 rounded-xl">
              <Building2 className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        {/* Clientes Previstos */}
        <Card className="border border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <TypographyMuted className="text-xs font-medium text-indigo-600 uppercase tracking-wider">
                Previstos
              </TypographyMuted>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totals.previstos}
              </p>
            </div>
            <div className="p-3 bg-indigo-100/80 text-indigo-700 rounded-xl">
              <Users className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        {/* Pesquisas Realizadas */}
        <Card className="border border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-white shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <TypographyMuted className="text-xs font-medium text-emerald-600 uppercase tracking-wider">
                Realizadas
              </TypographyMuted>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totals.realizadas}
              </p>
            </div>
            <div className="p-3 bg-emerald-100/80 text-emerald-700 rounded-xl">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        {/* Pesquisas Pendentes */}
        <Card className="border border-amber-100 bg-gradient-to-br from-amber-50/50 to-white shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <TypographyMuted className="text-xs font-medium text-amber-600 uppercase tracking-wider">
                Pendentes
              </TypographyMuted>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totals.pendentes}
              </p>
            </div>
            <div className="p-3 bg-amber-100/80 text-amber-700 rounded-xl">
              <Clock className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>

        {/* % Cobertura Geral */}
        <Card className="col-span-2 lg:col-span-1 border border-primary/20 bg-gradient-to-br from-primary/5 to-white shadow-sm">
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="flex items-center justify-between">
              <TypographyMuted className="text-xs font-medium text-primary uppercase tracking-wider">
                % Cobertura Geral
              </TypographyMuted>
              <span className="text-lg font-bold text-primary">
                {totals.cobertura.toFixed(1)}%
              </span>
            </div>
            <div className="mt-2 space-y-1">
              <Progress value={totals.cobertura} className="h-2 bg-gray-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Report Table (RF001 / RF002 / RF003) */}
      <Card className="border border-gray-200 shadow-sm overflow-hidden bg-white print:border-none print:shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700 font-semibold border-b border-gray-200 uppercase tracking-wider text-[11px] print:bg-gray-200">
                <th className="p-3.5 pl-4 sm:pl-6">Revenda</th>
                <th className="p-3.5">Pesquisa</th>
                <th className="p-3.5 text-center">Previstos</th>
                <th className="p-3.5 text-center">Realizadas</th>
                <th className="p-3.5 text-center">Pendentes</th>
                <th className="p-3.5 text-center w-48">% Cobertura</th>
                <th className="p-3.5 pr-4 sm:pr-6 text-center print:hidden">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {!hasSearched ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="p-3 bg-gray-100 rounded-full text-gray-400">
                        <Filter className="w-6 h-6" />
                      </div>
                      <p className="font-semibold text-gray-700">Nenhuma consulta realizada</p>
                      <p className="text-xs text-gray-500 max-w-sm">
                        Selecione os filtros acima e clique em <strong>Consultar</strong> para carregar o relatório de cobertura por revenda.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : filteredReport.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    Nenhum registro de cobertura encontrado.
                  </td>
                </tr>
              ) : (
                filteredReport.map((item, idx) => {
                  const perc = item.PERC_COBERTURA || 0;
                  let badgeColor = 'bg-red-100 text-red-800 border-red-200';
                  if (perc >= 100)
                    badgeColor = 'bg-blue-100 text-blue-800 border-blue-200';
                  else if (perc >= 75)
                    badgeColor =
                      'bg-emerald-100 text-emerald-800 border-emerald-200';
                  else if (perc >= 50)
                    badgeColor = 'bg-amber-100 text-amber-800 border-amber-200';

                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50/80 transition-colors"
                    >
                      {/* Revenda */}
                      <td className="p-3.5 pl-4 sm:pl-6">
                        <div className="font-semibold text-gray-900">
                          {item.DS_REVENDA}
                        </div>
                        <div className="text-[11px] text-gray-400 font-mono">
                          Cód: {item.SK_REVENDA}
                        </div>
                      </td>

                      {/* Pesquisa */}
                      <td className="p-3.5">
                        <span className="font-medium text-gray-800">
                          {item.NM_PESQUISA}
                        </span>
                      </td>

                      {/* Previstos */}
                      <td className="p-3.5 text-center font-semibold text-indigo-900 font-mono">
                        {item.QTD_CLIENTES_PREVISTOS}
                      </td>

                      {/* Realizadas */}
                      <td className="p-3.5 text-center font-semibold text-emerald-700 font-mono">
                        {item.QTD_PESQUISAS_REALIZADAS}
                      </td>

                      {/* Pendentes */}
                      <td className="p-3.5 text-center font-semibold text-amber-700 font-mono">
                        {item.QTD_PESQUISAS_PENDENTES}
                      </td>

                      {/* % Cobertura */}
                      <td className="p-3.5 text-center">
                        <div className="flex items-center gap-3 justify-center">
                          <Progress
                            value={perc}
                            className="h-2 w-24 bg-gray-100 print:hidden"
                          />
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold border ${badgeColor}`}
                          >
                            {perc.toFixed(1)}%
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-3.5 pr-4 sm:pr-6 text-center print:hidden">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenItemsDialog(item)}
                          className="h-8 gap-1 text-xs text-primary hover:text-primary hover:bg-primary/10"
                          title="Ver Detalhamento da Revenda"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {/* Table Footer with Total (RN004) */}
            {filteredReport.length > 0 && (
              <tfoot>
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300 text-gray-900 print:bg-gray-200">
                  <td className="p-3.5 pl-4 sm:pl-6" colSpan={2}>
                    TOTAL GERAL ({totals.totalResellerCount} Revendas)
                  </td>
                  <td className="p-3.5 text-center font-mono text-indigo-900">
                    {totals.previstos}
                  </td>
                  <td className="p-3.5 text-center font-mono text-emerald-700">
                    {totals.realizadas}
                  </td>
                  <td className="p-3.5 text-center font-mono text-amber-700">
                    {totals.pendentes}
                  </td>
                  <td className="p-3.5 text-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-black bg-primary text-white">
                      {totals.cobertura.toFixed(1)}%
                    </span>
                  </td>
                  <td className="print:hidden"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Detail Dialog */}
      <ResellerCoverageItemsDialog
        isOpen={isItemsDialogOpen}
        onClose={() => setIsItemsDialogOpen(false)}
        resellerData={selectedResellerForItems}
      />
    </div>
  );
};

export default ResellerCoverageReport;
